import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import './VendorLogin.css';

const VendorLogin = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showOTPVerification, setShowOTPVerification] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [pendingVendorData, setPendingVendorData] = useState(null);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    businessName: '',
    phone: '',
    otp: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
    setSuccess('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Use Supabase Auth for login
      const { data, error } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password
      });

      if (error) {
        setError(error.message);
        setLoading(false);
        return;
      }

      // Check if email is confirmed
      if (!data.user.email_confirmed_at) {
        setError('Please check your email and click the confirmation link before logging in.');
        setLoading(false);
        return;
      }

      // Check if user exists in vendors table and is approved
      const { data: vendor, error: vendorError } = await supabase
        .from('vendors')
        .select('*')
        .eq('email', formData.email)
        .single();

      if (vendorError || !vendor) {
        setError('No seller account found with this email address.');
        setLoading(false);
        return;
      }

      // Check if vendor is approved
      if (vendor.status !== 'active') {
        if (vendor.status === 'pending') {
          setError('Your email is confirmed! Your account is pending admin approval. Please wait for admin approval before logging in.');
        } else if (vendor.status === 'rejected') {
          setError('Your account has been rejected. Please contact admin for more information.');
        } else if (vendor.status === 'suspended') {
          setError('Your account is suspended. Please contact admin to reactivate.');
        } else {
          setError('Your account is not active. Please contact admin for assistance.');
        }
        setLoading(false);
        return;
      }

      // Store vendor info in localStorage for session management
      localStorage.setItem('vendorId', vendor.id);
      localStorage.setItem('vendorEmail', vendor.email);
      localStorage.setItem('vendorBusinessName', vendor.business_name);

      setSuccess('Login successful! Redirecting to seller dashboard...');
      setTimeout(() => {
        navigate('/vendor/dashboard');
      }, 2000);

    } catch (error) {
      console.error('Login error:', error);
      setError('An error occurred during login. Please try again.');
    }

    setLoading(false);
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    
    // Prevent multiple submissions
    if (loading) {
      return;
    }
    
    // Prevent signup if already in OTP verification
    if (showOTPVerification) {
      return;
    }

    setLoading(true);
    setError('');

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long.');
      setLoading(false);
      return;
    }

    try {
      // Check if vendor already exists in vendors table (any status)
      const { data: existingVendors, error: checkError } = await supabase
        .from('vendors')
        .select('id, status')
        .eq('email', formData.email);

      if (checkError && checkError.code !== 'PGRST116') { // PGRST116 is "not found" error
        console.error('Check error:', checkError);
        setError('An error occurred while checking vendor status. Please try again.');
        setLoading(false);
        return;
      }

      if (existingVendors && existingVendors.length > 0) {
        const existingVendor = existingVendors[0];
        
        if (existingVendor.status === 'pending') {
          setError('A vendor account with this email is already pending approval. Please wait for admin approval or contact support.');
        } else if (existingVendor.status === 'active') {
          setError('A vendor account with this email already exists and is active. Please login instead.');
        } else if (existingVendor.status === 'rejected') {
          setError('A vendor account with this email was previously rejected. Please contact admin for assistance.');
        } else if (existingVendor.status === 'suspended') {
          setError('A vendor account with this email is currently suspended. Please contact admin to reactivate your account.');
        } else {
          setError('A vendor account with this email already exists.');
        }
        setLoading(false);
        return;
      }

      // Use Supabase Auth to send OTP
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          emailRedirectTo: 'https://inifiy-2bc83.web.app/vendor/login',
          data: {
            business_name: formData.businessName,
            phone: formData.phone
          }
        }
      });

      if (authError) {
        setError(authError.message);
        setLoading(false);
        return;
      }

      // Store pending vendor data
      const vendorData = {
        email: formData.email,
        business_name: formData.businessName,
        phone: formData.phone,
        password: formData.password,
        status: 'pending'
      };

      setPendingVendorData(vendorData);
      setPendingEmail(formData.email);
      
      setSuccess('OTP sent to your email! Please check your inbox and enter the 6-digit code.');
      setShowOTPVerification(true);
      setFormData({
        ...formData,
        otp: ''
      });

    } catch (error) {
      console.error('Signup error:', error);
      setError('An error occurred during signup. Please try again.');
    }

    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Check if vendor exists first
      const { data: vendor, error: vendorError } = await supabase
        .from('vendors')
        .select('id, status')
        .eq('email', formData.email)
        .single();

      if (vendorError || !vendor) {
        setError('No seller account found with this email address.');
        setLoading(false);
        return;
      }

      // Check if vendor is active
      if (vendor.status !== 'active') {
        if (vendor.status === 'pending') {
          setError('Your account is pending approval. Please contact admin for assistance.');
        } else if (vendor.status === 'rejected') {
          setError('Your account has been rejected. Please contact admin for assistance.');
        } else if (vendor.status === 'suspended') {
          setError('Your account is suspended. Please contact admin to reactivate.');
        } else {
          setError('Your account is not active. Please contact admin for assistance.');
        }
        setLoading(false);
        return;
      }

      // Use Supabase Auth's built-in password reset
      const { error } = await supabase.auth.resetPasswordForEmail(formData.email, {
        redirectTo: 'https://inifiy-2bc83.web.app/vendor/reset-password'
      });

      if (error) {
        setError(error.message);
        setLoading(false);
        return;
      }

      setSuccess(`Password reset email sent to ${formData.email}. Please check your inbox and click the reset link.`);
      setShowForgotPassword(false);
      setIsLogin(true);
      setFormData({
        email: '',
        password: '',
        confirmPassword: '',
        businessName: '',
        phone: ''
      });

    } catch (error) {
      console.error('Forgot password error:', error);
      setError('An error occurred. Please try again.');
    }

    setLoading(false);
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setShowForgotPassword(false);
    setShowOTPVerification(false);
    setPendingEmail('');
    setPendingVendorData(null);
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      businessName: '',
      phone: '',
      otp: ''
    });
    setError('');
    setSuccess('');
  };

  const showForgotPasswordForm = () => {
    setShowForgotPassword(true);
    setError('');
    setSuccess('');
  };



  const handleOTPVerification = async (e) => {
    e.preventDefault();
    
    if (loading) return;
    
    setLoading(true);
    setError('');

    try {
      // Verify OTP with Supabase Auth
      const { data, error } = await supabase.auth.verifyOtp({
        email: pendingEmail,
        token: formData.otp,
        type: 'signup'
      });

      if (error) {
        setError(error.message);
        setLoading(false);
        return;
      }

      // Create vendor account in vendors table
      const { data: newVendor, error: insertError } = await supabase
        .from('vendors')
        .insert([pendingVendorData])
        .select()
        .single();

      if (insertError) {
        console.error('Insert error:', insertError);
        throw insertError;
      }

      // Send email notification to admin
      try {
        const { sendVendorSignupNotification } = await import('../utils/emailNotification.js');
        await sendVendorSignupNotification({
          email: pendingVendorData.email,
          business_name: pendingVendorData.business_name,
          phone: pendingVendorData.phone,
          id: newVendor.id
        });
      } catch (emailError) {
        console.error('Email notification error:', emailError);
      }

      setSuccess('Email verified successfully! Your account is pending admin approval. You will be able to login once approved.');
      setShowOTPVerification(false);
      setIsLogin(true);
      setPendingEmail('');
      setPendingVendorData(null);
      setFormData({
        email: '',
        password: '',
        confirmPassword: '',
        businessName: '',
        phone: '',
        otp: ''
      });

    } catch (error) {
      console.error('OTP verification error:', error);
      setError('An error occurred during verification. Please try again.');
    }

    setLoading(false);
  };

  const resendOTP = async () => {
    if (loading) return;
    
    setLoading(true);
    setError('');

    try {
      // Resend OTP using Supabase Auth
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: pendingEmail
      });

      if (error) {
        setError(error.message);
      } else {
        setSuccess('New OTP sent to your email! Please check your inbox.');
      }
    } catch (error) {
      console.error('Resend OTP error:', error);
      setError('Failed to resend OTP. Please try again.');
    }

    setLoading(false);
  };

  const backToLogin = () => {
    setShowForgotPassword(false);
    setShowOTPVerification(false);
    setIsLogin(true);
    setPendingEmail('');
    setPendingVendorData(null);
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      businessName: '',
      phone: '',
      otp: ''
    });
    setError('');
    setSuccess('');
  };



  // Render OTP verification form
  if (showOTPVerification) {
    return (
      <div className="vendor-login-page">
        <div className="vendor-login-hero">
          <div className="vendor-login-hero-content">
            <h1>Email Verification</h1>
            <p>Enter the OTP code sent to your email</p>
          </div>
        </div>

        <div className="vendor-login-container">
          <div className="vendor-login-content">
            <div className="auth-form">
              <h2>Verify Email</h2>
              
              {error && <div className="error-message">{error}</div>}
              {success && <div className="success-message">{success}</div>}

              <form onSubmit={handleOTPVerification}>
                <div className="form-group">
                  <label htmlFor="otp">OTP Code</label>
                  <input
                    type="text"
                    id="otp"
                    name="otp"
                    value={formData.otp}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter 6-digit OTP"
                    maxLength="6"
                    pattern="[0-9]{6}"
                  />
                </div>

                <button 
                  type="submit" 
                  className="submit-btn"
                  disabled={loading}
                >
                  {loading ? 'Verifying...' : 'Verify Email'}
                </button>
              </form>

              <div className="auth-info">
                <p>
                  Didn't receive the OTP? 
                  <button 
                    className="link-btn" 
                    onClick={resendOTP}
                    disabled={loading}
                  >
                    Resend OTP
                  </button>
                </p>
                <p>
                  <button 
                    className="link-btn" 
                    onClick={backToLogin}
                  >
                    Back to login
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render forgot password form
  if (showForgotPassword) {
    return (
      <div className="vendor-login-page">
        <div className="vendor-login-hero">
          <div className="vendor-login-hero-content">
            <h1>Forgot Password</h1>
            <p>Enter your email to reset your password</p>
          </div>
        </div>

        <div className="vendor-login-container">
          <div className="vendor-login-content">
            <div className="auth-form">
              <h2>Reset Password</h2>
              
              {error && <div className="error-message">{error}</div>}
              {success && <div className="success-message">{success}</div>}

              <form onSubmit={handleForgotPassword}>
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your email"
                  />
                </div>

                <button 
                  type="submit" 
                  className="submit-btn"
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </form>

              <div className="auth-info">
                <p>
                  Remember your password? 
                  <button 
                    className="link-btn" 
                    onClick={backToLogin}
                  >
                    Back to login
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="vendor-login-page">
      <div className="vendor-login-hero">
        <div className="vendor-login-hero-content">
          <h1>Seller Portal</h1>
          <p>Access your seller dashboard to manage your products and orders</p>
        </div>
      </div>

      <div className="vendor-login-container">
        <div className="vendor-login-content">
          <div className="auth-toggle">
            <button 
              className={`toggle-btn ${isLogin ? 'active' : ''}`}
              onClick={() => toggleMode()}
            >
              Login
            </button>
            <button 
              className={`toggle-btn ${!isLogin ? 'active' : ''}`}
              onClick={() => toggleMode()}
            >
              Sign Up
            </button>
          </div>

          <div className="auth-form">
            <h2>{isLogin ? 'Seller Login' : 'Seller Registration'}</h2>
            
            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            <form onSubmit={isLogin ? handleLogin : handleSignup}>
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your email"
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <div className="password-input-container">
                <input
                    type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your password"
                />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex="-1"
                    title={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                        <path d="M12 9c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3zm0 4c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z"/>
                        <line x1="2" y1="2" x2="22" y2="22" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {isLogin && (
                <div className="forgot-password-link">
                  <button 
                    type="button"
                    className="link-btn"
                    onClick={showForgotPasswordForm}
                  >
                    Forgot Password?
                  </button>
                </div>
              )}

              {!isLogin && (
                <>
                  <div className="form-group">
                    <label htmlFor="confirmPassword">Confirm Password</label>
                    <div className="password-input-container">
                    <input
                        type={showConfirmPassword ? "text" : "password"}
                      id="confirmPassword"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      required
                      placeholder="Confirm your password"
                    />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        tabIndex="-1"
                        title={showConfirmPassword ? "Hide password" : "Show password"}
                      >
                        {showConfirmPassword ? (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                            <path d="M12 9c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3zm0 4c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z"/>
                            <line x1="2" y1="2" x2="22" y2="22" stroke="currentColor" strokeWidth="2"/>
                          </svg>
                        ) : (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="businessName">Business Name</label>
                    <input
                      type="text"
                      id="businessName"
                      name="businessName"
                      value={formData.businessName}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your business name"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">Phone Number</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your phone number"
                    />
                  </div>
                </>
              )}

              <button 
                type="submit" 
                className="submit-btn"
                disabled={loading}
              >
                {loading ? 'Processing...' : (isLogin ? 'Login' : 'Sign Up')}
              </button>
            </form>

            <div className="auth-info">
              <p>
                {isLogin 
                  ? "Don't have a seller account? " 
                  : "Already have a seller account? "
                }
                <button 
                  className="link-btn" 
                  onClick={toggleMode}
                >
                  {isLogin ? 'Sign up here' : 'Login here'}
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorLogin; 